// Écran toujours allumé — Screen Wake Lock API.
//
// Pendant une séance, le téléphone est posé sur le sac entre deux balles. Sans
// verrou, il s'éteint au bout de trente secondes et il faut le déverrouiller à
// chaque série — ce qui suffit à faire abandonner la saisie.
//
// Le verrou est perdu dès que l'onglet passe en arrière-plan : le navigateur
// le relâche, et rien ne prévient. D'où la réacquisition sur visibilitychange,
// sans laquelle revenir sur l'application laisserait l'écran s'éteindre à
// nouveau alors que l'interface affiche toujours « actif ».

import { useCallback, useEffect, useRef, useState } from 'react';

export const wakeLockSupported = (): boolean =>
    typeof navigator !== 'undefined' && 'wakeLock' in navigator;

export function useWakeLock(enabled: boolean) {
    const sentinel = useRef<WakeLockSentinel | null>(null);
    const [active, setActive] = useState(false);

    const acquire = useCallback(async () => {
        if (!enabled || !wakeLockSupported() || document.visibilityState !== 'visible') return;
        try {
            sentinel.current = await navigator.wakeLock.request('screen');
            setActive(true);
            sentinel.current.addEventListener('release', () => setActive(false));
        } catch {
            // Batterie faible, ou refus de la plateforme : on continue sans.
            setActive(false);
        }
    }, [enabled]);

    const release = useCallback(async () => {
        try {
            await sentinel.current?.release();
        } catch { /* déjà relâché */ }
        sentinel.current = null;
        setActive(false);
    }, []);

    useEffect(() => {
        if (enabled) void acquire();
        else void release();

        const onVisibility = (): void => {
            if (document.visibilityState === 'visible' && enabled) void acquire();
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            void release();
        };
    }, [enabled, acquire, release]);

    return { active, supported: wakeLockSupported() };
}
